/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import InquiryToCustomer, { InquiryToCustomerStatus } from "../../../types/Entities/InquiryToCustomer";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { FormProvider, useForm } from "../../../provider/Form/FormProvider";
import { apiPatch } from "../../../services/apiClient";
import ButtonLoading from "../../Button/ButtonLoading";
import Group from "../../Wizard/Group";
import Paper from "@mui/material/Paper";
import TextContainer from "../../Container/TextContainer";
import wizardStyle from "../../Form/wizardStyle";
import { useInvalidateOpenInquiry } from "./useInvalidateOpenInquiry";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import InquiryToCustomerMediaObjects from "./InquiryToCustomerMediaObjects";

type InquiryToCustomerDecisionBoxProps = {
  inquiryToCustomer: InquiryToCustomer;
  product: AbstractCase;
};

export default function InquiryToCustomerDecisionBox({ inquiryToCustomer, product }: InquiryToCustomerDecisionBoxProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const invalidateOpenInquiry = useInvalidateOpenInquiry();

  let initialValues = {
    inquiryDecisionAnswer: null,
  };

  const handleSubmit = async ({ values }: { values: Partial<InquiryToCustomer> }) => {
    setIsLoading(true);
    let payload: Partial<InquiryToCustomer> = {
      status: InquiryToCustomerStatus.STATUS_DONE,
      doneBy: "customer",
      inquiryDecisionAnswer: values.inquiryDecisionAnswer,
    };

    await apiPatch("/inquiry_to_customers/" + inquiryToCustomer.id, payload);
    setIsLoading(false);

    await invalidateOpenInquiry(product);
  };

  return (
    <Paper sx={{ padding: "1rem" }}>
      <TextContainer>
        <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
          <InquiryToCustomerDecisionForm inquiryToCustomer={inquiryToCustomer} isLoading={isLoading} />
        </FormProvider>
      </TextContainer>
    </Paper>
  );
}

type InquiryToCustomerDecisionFormProps = {
  inquiryToCustomer: InquiryToCustomer;
  isLoading: boolean;
};

const InquiryToCustomerDecisionForm = ({ inquiryToCustomer, isLoading }: InquiryToCustomerDecisionFormProps) => {
  const { handleSubmit, values } = useForm();

  return (
    <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
      <Typography variant={"h4"} sx={{ textAlign: "center" }}>
        Benötigte Informationen
      </Typography>
      <InquiryToCustomerMediaObjects inquiryToCustomer={inquiryToCustomer} />
      <Grid
        container
        sx={{
          alignItems: "baseline",
          justifyContent: "center"
        }}>
        <Grid size={12}>
          <Box dangerouslySetInnerHTML={{ __html: inquiryToCustomer.inquiryDecisionQuestion ?? "" }} />
          <Group
            type="radio"
            name="inquiryDecisionAnswer"
            question={""}
            options={[
              {
                labelText: "Ja",
                labelIcon: <CheckIcon />,
                labelIconActive: <CheckIcon active />,
                value: "yes",
              },
              {
                labelText: "Nein",
                labelIcon: <CrossIcon />,
                labelIconActive: <CrossIcon active />,
                value: "no",
              },
            ]}
          />
        </Grid>
      </Grid>
      <Box sx={{ marginTop: "2rem" }}>
        <ButtonLoading fullWidth variant={"contained"} color={"primary"} type={"submit"} isLoading={isLoading} disabled={values.inquiryDecisionAnswer === null}>
          Rückmeldung abschicken
        </ButtonLoading>
      </Box>
    </Box>
  );
};
